import { useQuery } from '@tanstack/react-query'
import { ModuleHeader, SpinnerWithBackground, TabBar } from '@viapeople/components'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { filterTabsByRole } from '../../utils/tabFiltering'
import { trpc } from '../../utils/trpc'
import { useEnpsStore } from './store'

/**
 * @fileoverview Manage eNPS Page
 *
 * Main page for creating/editing eNPS configurations with tab navigation
 */

const tabs = [
	{ id: 'general', label: 'General' },
	{ id: 'configuration', label: 'Configuration' },
	{ id: 'schedule', label: 'Schedule' },
	{ id: 'population', label: 'Population' },
	{ id: 'email-template', label: 'Email Template' },
	{ id: 'launch', label: 'Launch' },
	{ id: 'results', label: 'Results' },
]

export const ManageEnpsPage = () => {
	const { enpsId } = useParams<{ enpsId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()

	// Check eNPS module permission
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('enps')

	// Redirect unauthorized users
	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate])

	const { data: enpsData, isLoading: isLoadingEnps } = useQuery({
		...trpc.client.enps.getById.queryOptions({
			id: enpsId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!enpsId && enpsId !== 'create' && !!organizationId,
	})

	const location = useLocation()
	const pathArray = location.pathname.split('/')
	const currentTab = pathArray?.[3] ?? 'general'

	const { enpsConfig, setEnpsConfig } = useEnpsStore()

	useEffect(() => {
		if (enpsData) {
			setEnpsConfig({
				id: enpsData.id,
				name: enpsData.name,
				description: enpsData.description,
				status: enpsData.status,
				organizationId: enpsData.organizationId,
				cadence: enpsData.cadence,
				populationId: enpsData.populationId,
				emailTemplateId: enpsData.emailTemplateId,
				questionText: enpsData.questionText,
				followUpQuestionText: enpsData.followUpQuestionText,
				textFeedbackEnabled: enpsData.textFeedbackEnabled ?? true,
				allowExternalParticipants: enpsData.allowExternalParticipants ?? false,
				nextScheduledDate: enpsData.nextScheduledDate ? new Date(enpsData.nextScheduledDate) : null,
				lastExecutionDate: enpsData.lastExecutionDate ? new Date(enpsData.lastExecutionDate) : null,
				createdAt: new Date(enpsData.createdAt),
				updatedAt: new Date(enpsData.updatedAt),
			})
		} else if (organizationId) {
			setEnpsConfig({
				id: '',
				name: '',
				description: null,
				status: 'draft',
				organizationId,
				cadence: 'quarterly',
				populationId: null,
				emailTemplateId: null,
				questionText: 'How likely are you to recommend our organization as a place to work?',
				followUpQuestionText: 'What is the primary reason for your score?',
				textFeedbackEnabled: true,
				allowExternalParticipants: false,
				nextScheduledDate: null,
				lastExecutionDate: null,
				createdAt: new Date(),
				updatedAt: new Date(),
			})
		}
	}, [enpsData, organizationId, setEnpsConfig])

	const handleTabChange = (tabId: string) => {
		const enpsPath = enpsId === 'create' || !enpsId ? 'create' : enpsId
		navigate(`/enps/${enpsPath}/${tabId}`)
	}

	const availableTabs = useMemo(() => filterTabsByRole(tabs, enpsId, role), [enpsId, role])

	// Show spinner while checking permissions or loading data
	if (isCheckingPermission || isLoadingEnps) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	// Don't render if not authorized (redirect will happen via useEffect)
	if (!isAuthorized) {
		return null
	}

	return (
		<main className='w-full p-6 min-h-full'>
			<div className='gap-6 flex flex-col'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Home', to: '/' },
						{ label: 'NPS', to: '/enps' },
						{
							label: enpsConfig?.name || (enpsId && enpsId !== 'create' ? 'Manage NPS' : 'Create NPS'),
						},
					]}
					title={enpsConfig?.name || (enpsId && enpsId !== 'create' ? 'Manage NPS' : 'Create NPS Survey')}
				/>
				<TabBar tabs={availableTabs} activeTab={currentTab} onChange={handleTabChange} />
			</div>
			<Outlet context={{ enpsConfig }} />
		</main>
	)
}
